/**
 * GroupRowsSection.jsx - Customize > Group rows (Sharif, 10/14/26: "add that
 * parent row as a customizable feature"). How the main project and the
 * sub-project rows look - each level its own row colour, text colour, bold,
 * text size, project # badge, roll-up sums and count badge - plus the
 * connector lines and how the groups start. Every change shows on the table
 * at once and is saved with the view (treeLook.js), shared with it like the
 * rest of the look. Reset puts the rows back as they shipped.
 */
import { useState } from 'react';
import { FONT_COLORS, HIGHLIGHT_COLORS } from '../highlights';
import { TREE_FILLS, TREE_SIZES, levelLook } from '../treeLook';
import { Switch, Segmented, ColorChoice, SectionHead, Row } from './controls';

const SELECT = 'border border-border-strong rounded px-2 py-1 text-[12px] bg-surface';

/** A sample of the row as it will look: the arrow, the project #, the name, the count and a sum. */
function Preview({ tree, level }) {
  const l = levelLook(tree, level);
  const main = level === 'main';
  return (
    <div
      data-testid="group-row-preview"
      className="rounded border border-border-strong overflow-hidden my-2"
      style={{ background: l.fill, color: l.font || '#0D0D0D', fontSize: l.size || 10.5, fontWeight: l.bold ? 600 : 400 }}
    >
      <div className="flex items-center gap-2 px-2 py-1.5">
        <span className="inline-flex items-center justify-center w-[18px] h-[18px] rounded border border-[#C5D3BC] bg-white text-[10px] text-[#1B5E20]">
          ›
        </span>
        {l.number && (
          <span
            className="font-mono rounded px-1.5 text-[10px] font-bold"
            style={{ color: main ? '#fff' : '#1B5E20', background: main ? '#2E7D32' : '#E3EEDA' }}
          >
            {main ? 'P26006-000' : 'P26006-030'}
          </span>
        )}
        <span style={{ fontWeight: l.bold ? (main ? 800 : 700) : 500 }}>
          {main ? 'Oshkosh 3003313 Process Support' : 'Additional Paint Site Support'}
        </span>
        {l.count && (
          <span className="rounded-full border border-[#C5D3BC] bg-white px-1.5 text-[9.5px] font-extrabold text-[#1B5E20]">
            {main ? 6 : 4}
          </span>
        )}
        <span className="ml-auto tabular-nums">{l.totals ? (main ? '$355,880.50' : '$235,932.00') : ''}</span>
      </div>
    </div>
  );
}

export default function GroupRowsSection({ view, hasGroups = true }) {
  const [level, setLevel] = useState('main');
  const tree = view.tree;
  const l = tree[level];
  const set = (patch) => view.setTree({ [level]: patch });
  const fills = { default: { label: 'default green', fill: TREE_FILLS[level] }, ...HIGHLIGHT_COLORS };
  return (
    <div>
      <SectionHead
        title="Group rows"
        hint="How the main project and sub-project rows look. Each change shows on the table straight away and is saved with the view."
        onReset={() => view.resetSection('tree')}
        resetLabel="Reset group rows"
      />
      {!hasGroups && (
        <p className="text-[11.5px] text-text-muted m-0 mb-2">
          Nothing is grouped in this table yet. These settings apply as soon as a project is put under another (its Main project).
        </p>
      )}
      <Segmented
        label="Which rows"
        value={level}
        onChange={setLevel}
        options={[
          ['main', 'Main project rows'],
          ['sub', 'Sub-project rows'],
        ]}
      />
      <Preview tree={tree} level={level} />
      <div className="py-2 space-y-2 border-b border-border">
        <ColorChoice label="Row colour" presets={fills} value={l.fill} onChange={(fill) => set({ fill })} />
        <ColorChoice
          label="Text colour"
          presets={FONT_COLORS}
          value={l.font === 'default' ? 'none' : l.font}
          onChange={(font) => set({ font: font === 'none' ? 'default' : font })}
          text
        />
        <p className="m-0 text-[10.5px] text-text-muted">∅ on the row colour is white; on the text colour, the table's own.</p>
      </div>
      <Row label="Bold" hint="The row and its name in bold.">
        <Switch on={l.bold} onChange={(bold) => set({ bold })} label={`Bold ${level} rows`} />
      </Row>
      <Row label="Text size">
        <select
          aria-label={`Text size of ${level} rows`}
          className={SELECT}
          value={l.size || ''}
          onChange={(e) => set({ size: e.target.value ? Number(e.target.value) : null })}
        >
          <option value="">The table&apos;s own</option>
          {TREE_SIZES.map((n) => (
            <option key={n} value={n}>
              {n}px
            </option>
          ))}
        </select>
      </Row>
      <Row label="Project # badge" hint="The Global Project Number before the name.">
        <Switch on={l.number} onChange={(number) => set({ number })} label={`Project number badge on ${level} rows`} />
      </Row>
      <Row label="Totals on the row" hint="The sums of the records under it (Quoted, PO $, Invoiced, Leftover...).">
        <Switch on={l.totals} onChange={(totals) => set({ totals })} label={`Totals on ${level} rows`} />
      </Row>
      <Row label="Count badge" hint="How many records it holds.">
        <Switch on={l.count} onChange={(count) => set({ count })} label={`Count badge on ${level} rows`} />
      </Row>
      <h5 className="m-0 mt-4 mb-1 text-[12px] font-bold text-text-dark">All group rows</h5>
      <Row label="Connector lines" hint="The └ that ties a record to its group; off: indent only.">
        <Switch on={tree.lines} onChange={(lines) => view.setTree({ lines })} label="Connector lines" />
      </Row>
      <Row label="Groups start" hint="Open or closed when the table opens; Remember keeps what you left open on this computer.">
        <Segmented
          label="Groups start"
          value={tree.open}
          onChange={(open) => view.setTree({ open })}
          options={[
            ['remember', 'Remember'],
            ['expanded', 'All open'],
            ['collapsed', 'All closed'],
          ]}
        />
      </Row>
    </div>
  );
}
